// The "a bank connection attempt is in flight" signal that survives the Pluggy
// OAuth hop. The hop navigates the tab to the bank and back, so nothing in React
// survives it — the marker does, in two carriers (mirroring chunk-reload.ts):
// sessionStorage preferred (per-tab, invisible, dies with the tab) and a URL
// query param as the fallback for storage-blocked browsers, appended to the
// OAuth redirect URI itself and stripped once consumed.

const CONNECT_ATTEMPT_STORAGE_KEY = 'vertice.onboarding.connect-attempt.v1';
export const CONNECT_RETURN_PARAM = '_ca';
export const CONNECT_TARGET_PARAM = '_ct';

export interface ConnectAttempt {
  armed: boolean;
  /** Item ids that existed BEFORE the attempt — the discriminator between a
   * stale pre-existing connection and the one this attempt creates. Null when
   * the stamp predates the field or only the URL carrier survived. */
  baselineItemIds: string[] | null;
  /** For update/reauth attempts: the item being re-authorized. It already
   * exists (so it sits in the baseline), but on the return it IS the expected
   * arrival — its post-authorization state resolves the window. Carried in
   * the URL too, so storage-blocked returns keep the reauth identity. */
  targetItemId: string | null;
}

/** Stamp an attempt before the connect flow opens — the OAuth hop may follow.
 * Carries the currently known item ids so the return can tell a freshly created
 * item apart from a stale one that happens to look adoptable.
 *
 * The stamp starts in the 'open' phase: the dialog is up but nothing was
 * authorized. Only promoteConnectAttempt (the widget handoff) makes it arm a
 * later mount — a reload with the dialog still open must not replay as a
 * return and 'complete' an update target that was never re-authorized. */
export function markConnectAttempt(
  knownItemIds: string[],
  targetItemId: string | null = null,
  now = Date.now(),
): void {
  try {
    window.sessionStorage.setItem(
      CONNECT_ATTEMPT_STORAGE_KEY,
      JSON.stringify({ t: now, items: knownItemIds, target: targetItemId, phase: 'open' }),
    );
  } catch {
    // Storage blocked — the URL marker on the redirect URI is the fallback carrier.
  }
}

/** The preflight handed off to the Pluggy widget — from here the OAuth hop can
 * navigate the tab at any moment, so the stamp becomes a real return marker. */
export function promoteConnectAttempt(): void {
  try {
    const raw = window.sessionStorage.getItem(CONNECT_ATTEMPT_STORAGE_KEY);
    if (raw === null) return;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return;
    window.sessionStorage.setItem(
      CONNECT_ATTEMPT_STORAGE_KEY,
      JSON.stringify({ ...(parsed as Record<string, unknown>), phase: 'launched' }),
    );
  } catch {
    // Storage blocked or an unreadable stamp — the URL carrier covers the return.
  }
}

export function clearConnectAttempt(): void {
  try {
    window.sessionStorage.removeItem(CONNECT_ATTEMPT_STORAGE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
}

/**
 * One-shot read: true when an attempt was stamped (either carrier). Consumes the
 * storage stamp so an abandoned attempt arms at most one discovery window on a
 * later visit; the URL carrier is stripped separately (stripConnectReturnMarker)
 * because the visible URL should only be rewritten from an effect.
 */
export function consumeConnectAttempt(): ConnectAttempt {
  let armed = false;
  let baselineItemIds: string[] | null = null;
  let targetItemId: string | null = null;
  try {
    const raw = window.sessionStorage.getItem(CONNECT_ATTEMPT_STORAGE_KEY);
    if (raw !== null) {
      // One-shot either way: an 'open'-phase stamp is an abandoned dialog
      // (reload before the handoff) — deleted without arming, it self-heals.
      window.sessionStorage.removeItem(CONNECT_ATTEMPT_STORAGE_KEY);
      try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null) {
          if ((parsed as { phase?: unknown }).phase === 'launched') {
            armed = true;
            if ('items' in parsed && Array.isArray((parsed as { items: unknown }).items)) {
              baselineItemIds = (parsed as { items: unknown[] }).items.filter(
                (id): id is string => typeof id === 'string',
              );
            }
            if ('target' in parsed && typeof (parsed as { target: unknown }).target === 'string') {
              targetItemId = (parsed as { target: string }).target;
            }
          }
        }
      } catch {
        // A pre-phase stamp (plain timestamp) cannot prove a handoff happened
        // — it does not arm. The URL carrier covers genuine in-flight returns.
      }
    }
  } catch {
    // Storage blocked — fall through to the URL carrier.
  }
  try {
    const params = new URL(window.location.href).searchParams;
    if (params.has(CONNECT_RETURN_PARAM)) {
      armed = true;
      // Storage wins when both carriers survive; the URL target only fills in
      // for the storage-blocked return.
      targetItemId ??= params.get(CONNECT_TARGET_PARAM);
    }
  } catch {
    // No usable URL — nothing to consume.
  }
  return { armed, baselineItemIds, targetItemId };
}

/** Takes the marker back out of the visible URL once consumed (chunk-reload.ts's pattern). */
export function stripConnectReturnMarker(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CONNECT_RETURN_PARAM) && !url.searchParams.has(CONNECT_TARGET_PARAM)) return;
    url.searchParams.delete(CONNECT_RETURN_PARAM);
    url.searchParams.delete(CONNECT_TARGET_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Cosmetic only — a leftover marker never re-arms (consumeConnectAttempt ran).
  }
}

/**
 * The OAuth redirect URI: back to the page the hop STARTED from. HTTPS only —
 * Pluggy rejects plain-http redirect URIs, so local dev sends none. On the
 * onboarding step the URI also carries the return marker, the storage-blocked
 * fallback that lets the fresh return load re-arm discovery.
 */
export function buildConnectRedirectUri(
  location: { origin: string; pathname: string },
  now = Date.now(),
  targetItemId: string | null = null,
): string | undefined {
  if (!location.origin.startsWith('https://')) return undefined;
  const base = `${location.origin}${location.pathname}`;
  if (location.pathname !== '/onboarding') return base;
  const target = targetItemId
    ? `&${CONNECT_TARGET_PARAM}=${encodeURIComponent(targetItemId)}`
    : '';
  return `${base}?${CONNECT_RETURN_PARAM}=${now}${target}`;
}
